/**
 * PdfSplitter — Miniatures 5 par ligne.
 * • Coche ronde haut-droite pour sélectionner/désélectionner
 * • Clic sur miniature → zoom haute résolution (flèches ← → pour naviguer, Échap pour fermer)
 * • Zones de coupure verticales/horizontales entre pages
 * Librairies : pdfjs-dist (rendu) + pdf-lib (reconstruction)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import { PDFDocument } from "pdf-lib";
import { Scissors, Loader2, X, RotateCcw, Trash2, Check, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

const COLS = 5;

/* ── Zone verticale ── */
function VertGap({ gapIdx, cuts, hoverGap, setHoverGap, toggleCut }) {
  const active = cuts.has(gapIdx), hovered = hoverGap === gapIdx;
  return (
    <div
      data-testid={`gap-${gapIdx}`}
      className="w-5 shrink-0 cursor-pointer relative self-stretch"
      onMouseEnter={() => setHoverGap(gapIdx)}
      onMouseLeave={() => setHoverGap(null)}
      onClick={(e) => { e.stopPropagation(); toggleCut(gapIdx); }}
    >
      {(active || hovered) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`w-0 h-full ${active ? "border-l-2 border-dashed border-red-500" : "border-l border-dashed border-zinc-400"}`} />
        </div>
      )}
      {active && (
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-10 bg-white pointer-events-none">
          <Scissors size={12} strokeWidth={2} className="text-red-500" />
        </div>
      )}
    </div>
  );
}

/* ── Zone horizontale ── */
function HorizGap({ gapIdx, cuts, hoverGap, setHoverGap, toggleCut }) {
  const active = cuts.has(gapIdx), hovered = hoverGap === gapIdx;
  return (
    <div
      data-testid={`gap-${gapIdx}`}
      className="h-5 cursor-pointer relative w-full"
      onMouseEnter={() => setHoverGap(gapIdx)}
      onMouseLeave={() => setHoverGap(null)}
      onClick={(e) => { e.stopPropagation(); toggleCut(gapIdx); }}
    >
      {(active || hovered) && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
          <div className={`flex-1 ${active ? "border-t-2 border-dashed border-red-500" : "border-t border-dashed border-zinc-400"}`} />
          <div className="mx-1.5 bg-white px-0.5">
            <Scissors size={active ? 13 : 12} strokeWidth={active ? 2 : 1.5} className={active ? "text-red-500" : "text-zinc-400"} />
          </div>
          <div className={`flex-1 ${active ? "border-t-2 border-dashed border-red-500" : "border-t border-dashed border-zinc-400"}`} />
        </div>
      )}
    </div>
  );
}

/* ── Composant principal ── */
export function PdfSplitter({ docId, filename, onDone, onCancel }) {
  const [thumbs, setThumbs]         = useState([]);
  const [loadState, setLoadState]   = useState("loading");
  const [cuts, setCuts]             = useState(new Set());
  const [selectedPages, setSelected]= useState(new Set());
  const [hoverGap, setHoverGap]     = useState(null);
  const [processing, setProcessing] = useState(false);
  // Zoom
  const [zoomIdx, setZoomIdx]       = useState(null);
  const [zoomUrl, setZoomUrl]       = useState(null);
  const pdfBytesRef                 = useRef(null);
  const pdfjsDocRef                 = useRef(null);

  /* ── Chargement du PDF ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/documents/${docId}/signed-url`);
        const resp     = await fetch(data.url);
        if (!resp.ok) throw new Error();
        pdfBytesRef.current = new Uint8Array(await resp.arrayBuffer());
        // Passer une COPIE à pdfjs pour éviter le transfert du buffer (Transferable)
        // qui détacherait pdfBytesRef.current et le rendrait illisible par pdf-lib
        const pdfjsDoc      = await pdfjs.getDocument({ data: pdfBytesRef.current.slice() }).promise;
        pdfjsDocRef.current = pdfjsDoc;
        const rendered = [];
        for (let p = 1; p <= pdfjsDoc.numPages; p++) {
          if (cancelled) return;
          const page     = await pdfjsDoc.getPage(p);
          const vp0      = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: 180 / vp0.width });
          const canvas   = document.createElement("canvas");
          canvas.width   = viewport.width; canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          rendered.push({ dataUrl: canvas.toDataURL("image/jpeg", 0.80), w: canvas.width, h: canvas.height });
          if (!cancelled) setThumbs([...rendered]);
        }
        if (!cancelled) setLoadState("ready");
      } catch { if (!cancelled) setLoadState("error"); }
    })();
    return () => { cancelled = true; };
  }, [docId]);

  /* ── Zoom haute résolution ── */
  const openZoom = useCallback(async (idx) => {
    setZoomIdx(idx);
    setZoomUrl(null);
    if (!pdfjsDocRef.current) return;
    const page     = await pdfjsDocRef.current.getPage(idx + 1);
    const vp0      = page.getViewport({ scale: 1 });
    // Rendu à ~900px de large pour le zoom
    const scale    = Math.min(900 / vp0.width, 2.5);
    const viewport = page.getViewport({ scale });
    const canvas   = document.createElement("canvas");
    canvas.width   = viewport.width; canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    setZoomUrl(canvas.toDataURL("image/jpeg", 0.94));
  }, []);

  const closeZoom = useCallback(() => { setZoomIdx(null); setZoomUrl(null); }, []);

  /* ── Navigation clavier dans le zoom ── */
  useEffect(() => {
    if (zoomIdx === null) return;
    const handler = (e) => {
      if (e.key === "Escape")      closeZoom();
      else if (e.key === "ArrowRight" && zoomIdx < thumbs.length - 1) openZoom(zoomIdx + 1);
      else if (e.key === "ArrowLeft"  && zoomIdx > 0)                 openZoom(zoomIdx - 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [zoomIdx, thumbs.length, openZoom, closeZoom]);

  const toggleCut  = useCallback((idx) => {
    setCuts((p) => { const n = new Set(p); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  }, []);
  const togglePage = useCallback((idx) => {
    setSelected((p) => { const n = new Set(p); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  }, []);

  /* ── Supprimer pages sélectionnées ── */
  const handleDeletePages = async () => {
    if (selectedPages.size >= thumbs.length) {
      toast.error("Impossible de supprimer toutes les pages.");
      return;
    }
    setProcessing(true);
    try {
      const srcDoc  = await PDFDocument.load(pdfBytesRef.current);
      const keepIdx = Array.from({ length: srcDoc.getPageCount() }, (_, i) => i)
                        .filter(i => !selectedPages.has(i));
      const newDoc  = await PDFDocument.create();
      (await newDoc.copyPages(srcDoc, keepIdx)).forEach(pg => newDoc.addPage(pg));
      const newBytes  = await newDoc.save();
      const baseName  = filename.replace(/\.pdf$/i, "").replace(/[^a-zA-Z0-9_\-]/g, "_");
      const formData  = new FormData();
      formData.append("files", new File([newBytes], `${baseName}_modifie.pdf`, { type: "application/pdf" }));
      // Ne pas forcer Content-Type : axios le gère avec le boundary correct
      await api.post("/documents/upload", formData);
      await api.delete(`/documents/${docId}`);
      toast.success(
        `${selectedPages.size} page${selectedPages.size > 1 ? "s" : ""} supprimée${selectedPages.size > 1 ? "s" : ""}. Nouveau document créé.`
      );
      onDone();
    } catch (err) {
      console.error("[PdfSplitter] handleDeletePages:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la suppression.");
    } finally {
      setProcessing(false);
    }
  };

  /* ── Découper en segments ── */
  const handleSplit = async () => {
    setProcessing(true);
    try {
      const sortedCuts = [...cuts].sort((a, b) => a - b);
      const srcDoc     = await PDFDocument.load(pdfBytesRef.current);
      const total      = srcDoc.getPageCount();
      const ranges     = []; let start = 0;
      for (const c of sortedCuts) { ranges.push({ from: start, to: c }); start = c + 1; }
      ranges.push({ from: start, to: total - 1 });
      const base = filename.replace(/\.pdf$/i, "").replace(/[^a-zA-Z0-9_\-]/g, "_");
      for (let i = 0; i < ranges.length; i++) {
        const { from, to } = ranges[i];
        const newDoc = await PDFDocument.create();
        (await newDoc.copyPages(srcDoc, Array.from({ length: to - from + 1 }, (_, k) => from + k)))
          .forEach(pg => newDoc.addPage(pg));
        const newBytes = await newDoc.save();
        const fd = new FormData();
        fd.append("files", new File([newBytes], `${base}_partie${i + 1}.pdf`, { type: "application/pdf" }));
        // Ne pas forcer Content-Type : axios le gère avec le boundary correct
        await api.post("/documents/upload", fd);
      }
      await api.delete(`/documents/${docId}`);
      toast.success(`${ranges.length} fichier${ranges.length > 1 ? "s" : ""} créé${ranges.length > 1 ? "s" : ""} dans la file.`);
      onDone();
    } catch (err) {
      console.error("[PdfSplitter] handleSplit:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la découpe.");
    } finally {
      setProcessing(false);
    }
  };

  const rows       = [];
  for (let i = 0; i < thumbs.length; i += COLS) rows.push(thumbs.slice(i, i + COLS));
  const segCount   = cuts.size + 1;
  const hasActions = cuts.size > 0 || selectedPages.size > 0;

  if (loadState === "error") return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-sm text-zinc-500">
      <p>Impossible de charger le PDF.</p>
      <button onClick={onCancel} className="underline text-xs">Retour</button>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* En-tête */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-zinc-200 bg-zinc-50 shrink-0 gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <Scissors size={14} strokeWidth={1.5} className="text-zinc-500" />
          <span className="text-xs font-mono text-zinc-600">
            {loadState === "loading" ? `Chargement… (${thumbs.length}p)` : `${thumbs.length} page${thumbs.length !== 1 ? "s" : ""}`}
          </span>
        </div>

        {/* Boutons d'action dans le header */}
        <div className="flex items-center gap-2 flex-1 justify-center">
          {selectedPages.size > 0 && (
            <button data-testid="delete-pages-btn" onClick={handleDeletePages} disabled={processing}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {processing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Supprimer {selectedPages.size} page{selectedPages.size > 1 ? "s" : ""}
            </button>
          )}
          {cuts.size > 0 && (
            <button data-testid="split-confirm-btn" onClick={handleSplit} disabled={processing}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-3 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {processing ? <Loader2 size={12} className="animate-spin" /> : <Scissors size={12} />}
              Découper en {segCount} fichier{segCount > 1 ? "s" : ""}
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {hasActions && (
            <button onClick={() => { setCuts(new Set()); setSelected(new Set()); }} title="Tout effacer"
              className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors">
              <RotateCcw size={13} />
            </button>
          )}
          <button onClick={onCancel} title="Annuler" className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Grille */}
      <div className="flex-1 overflow-y-auto app-scroll bg-zinc-100 p-5">
        {loadState === "loading" && thumbs.length === 0 && (
          <div className="flex items-center justify-center h-40">
            <Loader2 size={22} strokeWidth={1.5} className="animate-spin text-zinc-400" />
          </div>
        )}

        {rows.map((row, rowIdx) => {
          const lastOfRow = rowIdx * COLS + row.length - 1;
          return (
            <React.Fragment key={rowIdx}>
              <div className="flex items-stretch">
                {row.map((thumb, colIdx) => {
                  const pageIdx    = rowIdx * COLS + colIdx;
                  const isSelected = selectedPages.has(pageIdx);
                  return (
                    <React.Fragment key={pageIdx}>
                      {/* Miniature */}
                      <div
                        data-testid={`thumb-${pageIdx}`}
                        className="flex-1 min-w-0 relative group/thumb select-none"
                      >
                        {/* Corps — clic = zoom */}
                        <div
                          className="relative cursor-zoom-in"
                          onClick={() => openZoom(pageIdx)}
                        >
                          <img
                            src={thumb.dataUrl}
                            alt={`Page ${pageIdx + 1}`}
                            draggable={false}
                            className={`w-full block border bg-white shadow-sm transition-opacity ${
                              isSelected ? "border-[#002FA7] opacity-55" : "border-zinc-200"
                            }`}
                            style={{ aspectRatio: `${thumb.w}/${thumb.h}` }}
                          />
                          {/* Overlay loupe au survol */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity bg-black/15 pointer-events-none">
                            <ZoomIn size={22} strokeWidth={1.5} className="text-white drop-shadow-md" />
                          </div>
                          {/* Overlay sélection */}
                          {isSelected && (
                            <div className="absolute inset-0 bg-[#002FA7]/15 pointer-events-none" />
                          )}
                        </div>

                        {/* Coche ronde haut-droite */}
                        <button
                          data-testid={`select-page-${pageIdx}`}
                          title={isSelected ? "Désélectionner" : "Sélectionner"}
                          onClick={(e) => { e.stopPropagation(); togglePage(pageIdx); }}
                          style={{
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            minWidth: 20,
                            minHeight: 20,
                            padding: 0,
                            WebkitAppearance: "none",
                            appearance: "none",
                            overflow: "hidden",
                            border: isSelected ? "2px solid #002FA7" : "2px solid #9ca3af",
                            backgroundColor: isSelected ? "#002FA7" : "rgba(255,255,255,0.85)",
                          }}
                          className={`absolute top-1.5 right-1.5 inline-flex items-center justify-center z-10 shadow-sm shrink-0 transition-opacity
                            ${isSelected ? "opacity-100" : "opacity-0 group-hover/thumb:opacity-100"}`}
                        >
                          {isSelected && <Check size={10} strokeWidth={3} className="text-white" />}
                        </button>

                        {/* Numéro de page */}
                        <span className={`absolute bottom-1 left-1.5 text-[8px] font-mono px-1 py-0.5 rounded-sm pointer-events-none leading-none
                          ${isSelected ? "bg-[#002FA7] text-white" : "bg-black/40 text-white"}`}>
                          {pageIdx + 1}
                        </span>
                      </div>

                      {/* Zone de coupure verticale */}
                      {colIdx < row.length - 1 && (
                        <VertGap gapIdx={pageIdx} cuts={cuts} hoverGap={hoverGap} setHoverGap={setHoverGap} toggleCut={toggleCut} />
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Remplissage ligne incomplète */}
                {row.length < COLS && Array.from({ length: COLS - row.length }).map((_, k) => (
                  <React.Fragment key={`e${k}`}>
                    {k === 0 && <div className="w-5 shrink-0" />}
                    <div className="flex-1 min-w-0" />
                    {k < COLS - row.length - 1 && <div className="w-5 shrink-0" />}
                  </React.Fragment>
                ))}
              </div>

              {/* Zone de coupure horizontale */}
              {rowIdx < rows.length - 1 && (
                <HorizGap gapIdx={lastOfRow} cuts={cuts} hoverGap={hoverGap} setHoverGap={setHoverGap} toggleCut={toggleCut} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Pied — aide contextuelle uniquement */}
      <div className="px-5 py-2 border-t border-zinc-200 bg-zinc-50 shrink-0">
        <p className="text-center text-xs text-zinc-400 font-mono">
          Survol + clic = zoom · Coche = sélectionner · Entre les pages = découper
        </p>
      </div>

      {/* ── Lightbox zoom ── */}
      {zoomIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center"
          onClick={closeZoom}
        >
          {/* Navigation gauche */}
          {zoomIdx > 0 && (
            <button
              className="absolute left-4 p-2 text-white/70 hover:text-white transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); openZoom(zoomIdx - 1); }}
            >
              <ChevronLeft size={36} strokeWidth={1.5} />
            </button>
          )}

          {/* Image zoomée */}
          <div className="relative max-h-[90vh] max-w-[85vw] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {zoomUrl ? (
              <img
                src={zoomUrl}
                alt={`Page ${zoomIdx + 1}`}
                className="max-h-[90vh] max-w-[85vw] object-contain shadow-2xl"
              />
            ) : (
              <div className="w-64 h-80 flex items-center justify-center">
                <Loader2 size={28} strokeWidth={1.5} className="animate-spin text-white/50" />
              </div>
            )}
          </div>

          {/* Navigation droite */}
          {zoomIdx < thumbs.length - 1 && (
            <button
              className="absolute right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); openZoom(zoomIdx + 1); }}
            >
              <ChevronRight size={36} strokeWidth={1.5} />
            </button>
          )}

          {/* Fermer + info */}
          <button className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors" onClick={closeZoom}>
            <X size={20} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs font-mono">
            Page {zoomIdx + 1} / {thumbs.length} · Échap pour fermer · ← → pour naviguer
          </div>
        </div>
      )}
    </div>
  );
}
